// Data collectors.
//
// In a full production system these run as scheduled server-side jobs. Here they
// run client-side on a throttled schedule and persist observations locally, so
// the browser is not responsible for *continuous* monitoring — data is fetched,
// stored, and change-detection compares against the stored history.
//
// Collectors fetch real, free/public data where CORS permits (via the CORS
// proxy). When a feed is unavailable, the indicator is marked `unavailable`
// (never silently stale) and a deterministic reference value is used only for
// indicators that have no free public real-time feed.

import { INDICATORS, deriveHormuzRisk } from './indicators';
import type { Observation, DataQuality } from './types';

const CORS = (url: string) =>
  `https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`;

const FRESH_MS = 6 * 60 * 60 * 1000; // 6 hours

export function qualityFor(timestamp: number): DataQuality {
  if (!timestamp) return 'unavailable';
  return Date.now() - timestamp <= FRESH_MS ? 'fresh' : 'stale';
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(CORS(url), { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

interface CoinGeckoMarket {
  current_price: number;
  total_volume: number;
  price_change_percentage_30d_in_currency?: number;
  price_change_percentage_24h?: number;
}

interface Collected {
  values: Record<string, number>;
  liveIds: Set<string>;
}

/**
 * Fetch the crypto / BCH block from CoinGecko. Returns partial data; missing
 * fields simply won't be present in `values`.
 */
async function collectCrypto(signal?: AbortSignal): Promise<Partial<Collected>> {
  const values: Record<string, number> = {};
  const liveIds = new Set<string>();

  try {
    const markets = await fetchJson<
      (CoinGeckoMarket & { id: string })[]
    >(
      'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=bitcoin-cash,bitcoin&price_change_percentage=24h,30d',
      signal,
    );
    const bch = markets.find((m) => m.id === 'bitcoin-cash');
    const btc = markets.find((m) => m.id === 'bitcoin');

    if (bch) {
      values.bch_usd = bch.current_price;
      values.bch_volume = bch.total_volume / 1e9;
      if (typeof bch.price_change_percentage_30d_in_currency === 'number') {
        values.bch_momentum = bch.price_change_percentage_30d_in_currency;
      }
      liveIds.add('bch_usd').add('bch_volume');
      if ('bch_momentum' in values) liveIds.add('bch_momentum');
    }
    if (btc) {
      if (typeof btc.price_change_percentage_30d_in_currency === 'number') {
        values.btc_trend = btc.price_change_percentage_30d_in_currency;
        liveIds.add('btc_trend');
      }
    }
    if (bch && btc) {
      values.bch_btc = bch.current_price / btc.current_price;
      liveIds.add('bch_btc');
      if ('bch_momentum' in values && 'btc_trend' in values) {
        values.bch_rel_strength = values.bch_momentum - values.btc_trend;
        liveIds.add('bch_rel_strength');
      }
    }
  } catch {
    // leave crypto block empty; marked unavailable downstream
  }

  try {
    const global = await fetchJson<{
      data: {
        total_market_cap: { usd: number };
        market_cap_percentage: { btc: number };
      };
    }>('https://api.coingecko.com/api/v3/global', signal);
    values.total_mcap = global.data.total_market_cap.usd / 1e12;
    values.btc_dominance = global.data.market_cap_percentage.btc;
    liveIds.add('total_mcap').add('btc_dominance');
  } catch {
    /* ignore */
  }

  return { values, liveIds };
}

/**
 * Fetch FX (USD/JPY, DXY proxy). Uses the SAME source (Frankfurter/ECB) and the
 * SAME DXY formula as the server collector so a client-fallback reading can
 * never diverge from a server reading and trigger a false change-detection
 * spike. This path only runs when the server function is unreachable.
 */
async function collectFx(signal?: AbortSignal): Promise<Partial<Collected>> {
  const values: Record<string, number> = {};
  const liveIds = new Set<string>();
  try {
    const data = await fetchJson<{ rates?: Record<string, number> }>(
      'https://api.frankfurter.dev/v1/latest?base=USD&symbols=JPY,EUR,GBP,CAD,SEK,CHF',
      signal,
    );
    const r = data.rates;
    if (r?.JPY) {
      values.usdjpy = r.JPY;
      liveIds.add('usdjpy');
    }
    if (r?.EUR && r?.JPY && r?.GBP && r?.CAD && r?.SEK && r?.CHF) {
      // ICE DXY approximation. EUR/GBP are quoted as USD-per-unit (invert the
      // USD-base rates) with negative exponents — identical to the server.
      const eurusd = 1 / r.EUR;
      const gbpusd = 1 / r.GBP;
      const dxy =
        50.14348112 *
        Math.pow(eurusd, -0.576) *
        Math.pow(r.JPY, 0.136) *
        Math.pow(gbpusd, -0.119) *
        Math.pow(r.CAD, 0.091) *
        Math.pow(r.SEK, 0.042) *
        Math.pow(r.CHF, 0.036);
      values.dxy = dxy;
      liveIds.add('dxy');
    }
  } catch {
    /* ignore */
  }
  return { values, liveIds };
}

/**
 * Reference values for indicators without a free, CORS-friendly real-time feed
 * (macro series usually require an API key). These are clearly attributed and
 * only used as a baseline; live feeds always take precedence. In production
 * these come from FRED / Treasury / BOJ collectors on the server.
 */
const REFERENCE: Record<string, number> = {
  fed_balance_sheet: 7.05,
  fima_repo: 18,
  fed_fx_assets: 0.15,
  tga: 720,
  // ON RRP facility has largely drained since its 2022-23 peak (~$2.5trn);
  // it now runs near zero. Kept as an indicative baseline only.
  reverse_repo: 15,
  sofr: 4.33,
  jgb_10y: 2.9,
  japan_ust_holdings: 1.19,
  ust_2y: 4.18,
  ust_10y: 4.28,
  global_liquidity_proxy: 30.8,
  stablecoin_supply: 300,
  wti_crude: 72,
  brent_crude: 76,
  brent_wti_spread: 4,
  hormuz_risk: deriveHormuzRisk(76, 4) ?? 0,
  usdjpy: 151.5,
  dxy: 103.6,
  bch_usd: 204,
  bch_btc: 0.0031,
  bch_volume: 0.42,
  bch_momentum: 6,
  bch_rel_strength: 2,
  btc_trend: 4,
  btc_dominance: 56.5,
  total_mcap: 2.35,
};

export interface CollectResult {
  observations: Observation[];
  liveCount: number;
  attemptedLive: boolean;
  /** true when the server-side Netlify collector supplied the data */
  serverSourced: boolean;
}

interface ServerObservation {
  value: number;
  timestamp: number;
  source: string;
  quality: DataQuality;
}

interface ServerResponse {
  updatedAt: number;
  liveCount: number;
  observations: Record<string, ServerObservation>;
}

/**
 * Try the server-side Netlify collector first. It returns real macro data
 * (FRED / US Treasury) plus crypto & FX with per-indicator source + timestamp.
 */
async function collectFromServer(
  signal?: AbortSignal,
): Promise<{ live: Record<string, ServerObservation> } | null> {
  try {
    const res = await fetch('/.netlify/functions/collect', { signal });
    if (!res.ok) return null;
    const data = (await res.json()) as ServerResponse;
    if (!data || typeof data.observations !== 'object') return null;
    return { live: data.observations };
  } catch {
    return null;
  }
}

/**
 * Run all collectors and produce a full set of observations for every
 * indicator.
 *
 * Priority per indicator:
 *   1. Server-side Netlify collector (real macro + crypto + FX)
 *   2. Direct client fetch (crypto/FX only — used in local dev)
 *   3. Attributed reference baseline (marked stale)
 *   4. Unavailable (no value)
 */
export async function collectAll(signal?: AbortSignal): Promise<CollectResult> {
  const now = Date.now();

  // 1. Server-side collector (preferred).
  const server = await collectFromServer(signal);
  const serverLive = server?.live ?? {};
  const serverSourced = server !== null && Object.keys(serverLive).length > 0;

  // 2. Client-side crypto/FX fallback (fills gaps if the function is absent,
  //    e.g. during local `vite` dev where functions don't run).
  const clientVals: Record<string, number> = {};
  const clientLive = new Set<string>();
  if (!serverSourced) {
    try {
      const results = await Promise.all([
        collectCrypto(signal),
        collectFx(signal),
      ]);
      for (const r of results) {
        if (r.values) Object.assign(clientVals, r.values);
        if (r.liveIds) r.liveIds.forEach((id) => clientLive.add(id));
      }
    } catch {
      /* ignore */
    }
  }

  const liveIds = new Set<string>();

  const observations: Observation[] = INDICATORS.map((def) => {
    // Server value wins.
    const s = serverLive[def.id];
    if (s && typeof s.value === 'number') {
      liveIds.add(def.id);
      return {
        indicatorId: def.id,
        value: s.value,
        timestamp: s.timestamp || now,
        source: s.source || def.source,
        frequency: def.frequency,
        quality: s.quality || qualityFor(s.timestamp || now),
      };
    }

    // Client fallback value.
    if (clientLive.has(def.id) && typeof clientVals[def.id] === 'number') {
      liveIds.add(def.id);
      return {
        indicatorId: def.id,
        value: clientVals[def.id],
        timestamp: now,
        source: def.source,
        frequency: def.frequency,
        quality: 'fresh',
      };
    }

    // Reference baseline (attributed, marked stale). Its timestamp is 0 (not
    // `now`) so it can never masquerade as a freshly-updated reading — a
    // baseline is indicative, not a live observation at this moment.
    const ref = REFERENCE[def.id];
    return {
      indicatorId: def.id,
      value: typeof ref === 'number' ? ref : null,
      timestamp: 0,
      source:
        typeof ref === 'number'
          ? `${def.source} · reference baseline`
          : def.source,
      frequency: def.frequency,
      quality: typeof ref === 'number' ? 'stale' : 'unavailable',
    };
  });

  // Derive the Strait of Hormuz risk index from the resolved Brent price and
  // Brent–WTI spread. When the server already supplied a value it wins; here we
  // recompute from live oil signals so the proxy tracks real oil-market stress
  // rather than the static reference baseline.
  //
  // The index can only be as trustworthy as the oil data it is built from, so
  // it inherits the WORST freshness of its two inputs and the OLDEST timestamp.
  // If either input is a reference baseline / stale / unavailable, the index is
  // never presented as live "fresh" data.
  if (!serverLive.hormuz_risk) {
    const byId = new Map(observations.map((o) => [o.indicatorId, o]));
    const brentObs = byId.get('brent_crude');
    const spreadObs = byId.get('brent_wti_spread');
    const risk = deriveHormuzRisk(
      brentObs?.value ?? null,
      spreadObs?.value ?? null,
    );
    const hormuz = byId.get('hormuz_risk');
    if (hormuz && typeof risk === 'number') {
      // Worst-of the input qualities: unavailable > stale > fresh.
      const rank: Record<DataQuality, number> = {
        unavailable: 2,
        stale: 1,
        fresh: 0,
      };
      const inputs = [brentObs, spreadObs].filter(
        (o): o is Observation => !!o,
      );
      const worst = inputs.reduce<DataQuality>(
        (acc, o) => (rank[o.quality] > rank[acc] ? o.quality : acc),
        'fresh',
      );
      // 40% of the blend is the Brent–WTI spread. When it is missing (or null)
      // deriveHormuzRisk substitutes a fixed guess (4), so the index is only
      // partially real and must never read as fully "fresh" — degrade to at
      // least "stale".
      const hasSpread =
        !!spreadObs && spreadObs.value !== null;
      const worstWithSpread: DataQuality =
        !hasSpread && worst === 'fresh' ? 'stale' : worst;
      // If Brent itself is missing we cannot derive an honest index.
      const quality: DataQuality =
        !brentObs || brentObs.value === null
          ? 'unavailable'
          : worstWithSpread;

      hormuz.value = risk;
      hormuz.timestamp = inputs.length
        ? Math.min(...inputs.map((o) => o.timestamp || now))
        : now;
      hormuz.source = 'Derived (Brent + Brent–WTI)';
      hormuz.quality = quality;
      if (quality === 'fresh') liveIds.add('hormuz_risk');
    }
  }

  return {
    observations,
    liveCount: liveIds.size,
    attemptedLive: true,
    serverSourced,
  };
}
